import {ResponseDto} from './dto/ResponseDto';
import {LocalRecipient} from './LocalRecipient';
import {
    MessageSender
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/MessageSender.js';
import {
    WebSocketSpecificData
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/WebSocket/WebSocketSpecificData.js';
import {
    TypingTimeoutCalculator
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/TypingTimeoutCalculator.js';
import {
    TextItem
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/TextItem.js';
import {QuickReply} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/QuickReply.js';
import {
    WebSocketServerResponseDtoCreator
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/WebSocket/dto/WebSocketServerResponseDtoCreator.js';
import {Timeout} from 'botato-botengine-browser/botato/botato-botengine/source/Common/Helper/Timeout.js';
import {
    ImageUrlItem
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/ImageUrlItem.js';
import {
    SelectionList
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/SelectionList/SelectionList.js';
import {SentMessage} from 'botato-botengine-browser/botato/botato-botengine/source/Core/Session/SentMessage.js';
import {
    MultiSelectionList
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/MultiSelectionList/MultiSelectionList.js';
import {
    LinksItem
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/Links/LinksItem.js';
import {
    ResponseItem
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/ResponseItem.js';
import {
    LocationRequestForm
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/LocationRequestForm.js';
import {
    VerticalSelectionList
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/SelectionList/VerticalSelectionList.js';
import {
    AccountLinkingForm
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/AccountLinkingForm.js';
import {
    NameRequestForm
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/NameRequestForm.js';
import {
    ClosingItem
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/ClosingItem.js';
import {Card} from 'botato-botengine-browser/botato/botato-botengine/source/Core/BotResult/ResponseItem/Card/Card.js';

export class LocalMessageSender implements MessageSender<LocalRecipient, WebSocketSpecificData> {

    // The local sender has no message id to report back, so every send resolves with undefined as it
    // always did; the botengine's MessageSender interface nevertheless promises a SentMessage.
    private static readonly NO_SENT_MESSAGE = undefined as unknown as SentMessage;

    constructor(private typingTimeoutCalculator: TypingTimeoutCalculator) {
    }

    public async sendText(recipient: LocalRecipient, textItem: TextItem, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        this.startTyping(recipient);

        return Timeout.wait(this.typingTimeoutCalculator.calculate(textItem.text)).then((): SentMessage => {
            recipient.messageReceived.emit(WebSocketServerResponseDtoCreator.createTextMessage(textItem, this.toDtoQuickReplies(quickReplies), this.toDtoWebSocketSpecificData(webSocketSpecificData)) as ResponseDto);
            return LocalMessageSender.NO_SENT_MESSAGE;
        });
    }

    public async sendImage(recipient: LocalRecipient, imageItem: ImageUrlItem, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        return Timeout.wait(this.typingTimeoutCalculator.getCustomTypingDuration()).then((): SentMessage => {
            recipient.messageReceived.emit(WebSocketServerResponseDtoCreator.createImageMessage(imageItem, this.toDtoQuickReplies(quickReplies), this.toDtoWebSocketSpecificData(webSocketSpecificData)) as ResponseDto);
            return LocalMessageSender.NO_SENT_MESSAGE;
        });
    }

    public async sendSelectionList(recipient: LocalRecipient, selectionList: SelectionList, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        return Timeout.wait(this.typingTimeoutCalculator.getCustomTypingDuration()).then((): SentMessage => {
            recipient.messageReceived.emit(WebSocketServerResponseDtoCreator.createSelectionListMessage(selectionList, this.toDtoQuickReplies(quickReplies), this.toDtoWebSocketSpecificData(webSocketSpecificData)) as ResponseDto);
            return LocalMessageSender.NO_SENT_MESSAGE;
        });
    }

    public async sendMultiSelectionList(recipient: LocalRecipient, multiSelectionList: MultiSelectionList, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        return Timeout.wait(this.typingTimeoutCalculator.getCustomTypingDuration()).then((): SentMessage => {
            recipient.messageReceived.emit(WebSocketServerResponseDtoCreator.createMultiSelectionListMessage(multiSelectionList, this.toDtoQuickReplies(quickReplies), this.toDtoWebSocketSpecificData(webSocketSpecificData)) as ResponseDto);
            return LocalMessageSender.NO_SENT_MESSAGE;
        });
    }

    public async sendLinks(recipient: LocalRecipient, linksItem: LinksItem, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        return Timeout.wait(this.typingTimeoutCalculator.getCustomTypingDuration()).then((): SentMessage => {
            recipient.messageReceived.emit(WebSocketServerResponseDtoCreator.createLinksMessage(linksItem, this.toDtoQuickReplies(quickReplies), this.toDtoWebSocketSpecificData(webSocketSpecificData)) as ResponseDto);
            return LocalMessageSender.NO_SENT_MESSAGE;
        });
    }


    public async sendCustom(recipient: LocalRecipient, responseItem: ResponseItem, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        return Timeout.wait(this.typingTimeoutCalculator.getCustomTypingDuration()).then((): SentMessage => {
            recipient.messageReceived.emit(WebSocketServerResponseDtoCreator.createCustomMessage(responseItem, this.toDtoQuickReplies(quickReplies), this.toDtoWebSocketSpecificData(webSocketSpecificData)) as ResponseDto);
            return LocalMessageSender.NO_SENT_MESSAGE;
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/require-await
    public async sendLocationRequestForm(recipient: LocalRecipient, locationRequestForm: LocationRequestForm, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        return LocalMessageSender.NO_SENT_MESSAGE;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/require-await
    public async sendVerticalSelectionList(recipient: LocalRecipient, verticalSelectionList: VerticalSelectionList, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        console.log('No implementation for VerticalSelectionList on Botato-Angular-lib');
        return LocalMessageSender.NO_SENT_MESSAGE;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/require-await
    public async sendCard(recipient: LocalRecipient, card: Card, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        console.log('No implementation for Card on Botato-Angular-lib');
        return LocalMessageSender.NO_SENT_MESSAGE;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/require-await
    public async sendAccountLinkingForm(recipient: LocalRecipient, accountLinkingRequest: AccountLinkingForm, quickReplies?: QuickReply[], webSocketSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        console.log('No implementation for AccountLinkingForm on Botato-Angular-lib');
        return LocalMessageSender.NO_SENT_MESSAGE;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/require-await
    public async sendNameRequestForm(recipient: LocalRecipient, nameRequestForm: NameRequestForm, quickReplies?: QuickReply[], adapterSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        console.log('No implementation for NameRequestForm on Botato-Angular-lib');
        return LocalMessageSender.NO_SENT_MESSAGE;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/require-await
    public async sendClosing(recipient: LocalRecipient, closingItem: ClosingItem, adapterSpecificData?: WebSocketSpecificData): Promise<SentMessage> {
        console.log('No implementation for Closing on Botato-Angular-lib');
        return LocalMessageSender.NO_SENT_MESSAGE;
    }

    // The botengine's dto creator declares the quick replies and the adapter specific data as required
    // although the MessageSender interface hands them over optionally and the created dto simply carries undefined.
    private toDtoQuickReplies(quickReplies: QuickReply[] | undefined): QuickReply[] {
        return quickReplies as QuickReply[];
    }

    private toDtoWebSocketSpecificData(webSocketSpecificData: WebSocketSpecificData | undefined): WebSocketSpecificData {
        return webSocketSpecificData as WebSocketSpecificData;
    }

    private startTyping(recipient: LocalRecipient): void {
        recipient.startTyping.emit();
    }
}
